import React, { useEffect, useState, ReactNode } from 'react';
import styled from '@emotion/styled';
import { useProfile, useUser } from 'common/CacheProvider';
import { Link, Button, Placeholder } from 'common/styles';
import {
  IconFilledBookmark,
  IconUser,
  IconSubscription,
} from 'common/icons';
import Menu from 'common/Menu';
import ProfileMenu from 'common/ProfileMenu';
import { __t } from 'common/i18n';
import { useTheme } from '@emotion/react';
import { useRouter } from 'next/router';
import SubscriptionTooltip from 'common/SubscriptionTooltip';
import IconUserSetting from '../icons/IconUserSetting';
import UserAvatar from './UserAvatar';
import { replacePhoneNumberPrefix } from '../utils';

const StyledMenu = styled(Menu)(() => ({ width: '18.75rem' }));

const AvatarButton = styled.a(({ theme }) => ({
  display: 'block',
  cursor: 'pointer',
  padding: 0,
  fontSize: theme.spacing(4),
  borderRadius: '10rem',
  overflow: 'hidden',
  position: 'relative',
}));
type linkType = { label: string, icon: ReactNode, href: string };

export default function UserMenu({ scrollToTop }: { scrollToTop?: boolean }) {
  const { user, hasSubscription, loaded, subscriptionRemainingDays } = useUser();
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { profile } = useProfile();
  const subscriptionButtonMessage = (!hasSubscription || subscriptionRemainingDays <= 0) ? 'subscription.buy' : 'subscription.extend';
  const [links, setLinks] = useState<linkType[]>([
    // {
    //   label: subscriptionButtonMessage,
    //   icon: <IconSubscription />,
    //   href: '/packages',
    // },
    {
      label: 'profile.bookmarks',
      icon: <IconFilledBookmark />,
      href: '/user/bookmarks',
    },
    {
      label: 'profile.title',
      icon: <IconUser />,
      href: '/user',
    },
  ]);
  useEffect(() => {
    const _setting = {
      label: 'profile.edit.profiles',
      icon: <IconUserSetting />,
      href: '/profile/choose',
      // href: `/profile/choose?redirect=${router.asPath}`,
    };
    if (profile && String(profile.isPrimary) === 'true') {
      const isDuplicate = links?.some((item) => item.label === _setting.label && item.href === _setting.href);
      if (!isDuplicate) {
        setLinks((prevState) => [_setting, ...prevState]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  useEffect(() => {
    if (loaded && user) {
      setLinks((prevState) => [{
        label: subscriptionButtonMessage,
        icon: <IconSubscription />,
        href: '/packages',
      }, ...prevState]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const router = useRouter();
  const loginUrl = `/login?redirect=${router.asPath}`;
  const { breakpoints } = useTheme();

  const handleClick = () => {
    if (window.innerWidth < breakpoints.md) {
      router.push('/user/menu');
    } else {
      setOpen((prev) => !prev);
    }
  };

  if (!loaded) return <Loading redirect={loginUrl} />;
  if (!user) return <LoginButton redirect={loginUrl} />;

  const showPhoneTooltip = router.pathname === '/packages' && user && user.msisdn;

  const handleShowTooltip = (subscribe, days): boolean => {
    if (subscribe) {
      if (days === 0) {
        return false;
      } else if (days > 0 && days <= 5) {
        return true;
      }
      return false;
    }
    return true;
  };
  return (
    <>
      <div style={{ position: 'relative' }}>
        <AvatarButton ref={setReferenceElement} onClick={handleClick}>
          <UserAvatar user={user} size="small" />
        </AvatarButton>
        {showPhoneTooltip && <MsidnBox>{replacePhoneNumberPrefix(Number(user?.msisdn))}</MsidnBox>}
        {loaded && handleShowTooltip(hasSubscription, subscriptionRemainingDays)
          ? <div style={{ transition: 'top 0.2s ease-in-out', left: '-12px', position: 'absolute', top: scrollToTop ? '-20px' : '32px' }}>
            <SubscriptionTooltip days={subscriptionRemainingDays} />
          </div> : null}
      </div>
      <StyledMenu onClose={() => setOpen(false)} anchor={referenceElement} open={open} placement="bottom-end"
        wrapperStyles={{ zIndex: 1299 }} strategy="fixed"
      >
        <ProfileMenu user={user} links={links} showStatus isPopup />
      </StyledMenu>
    </>
  );
}

type LoadingButtonProps = { redirect: string };

const LoginButton = ({ redirect }: LoadingButtonProps) => (
  <Button variant="secondary" inputSize="small" as={Link} href={redirect}>
    {__t('auth.login')}
  </Button>
);

const Loading = ({ redirect }: LoadingButtonProps) => (
  <AvatarButton as={Link} href={redirect}>
    <Placeholder width={1} />
  </AvatarButton>
);

const MsidnBox = styled.div(({ theme }) => ({
  order: 0,
  height: '32px',
  textAlign: 'center',
  fontSize: '0.875rem',
  position: 'absolute',
  backgroundColor: theme.rgba(theme.palette.success[900][0], 0.08),
  border: `1px solid ${theme.rgba(theme.palette.success[900][0], 0.5)}`,
  color: theme.palette.success[900][0],
  borderRadius: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  width: '9rem',
  justifyContent: 'center',
  top: '-2px',
  left: '40px',
  fontWeight: 'bold',
  paddingTop: '4px',
}));
